import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, Save, FileDown, AlertTriangle, TrendingUp, BarChart, CheckCircle, XCircle, Edit2, Users, UserCheck, UserX } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { format } from 'date-fns';
import { BarChart as ReBarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const AttendanceMarking = () => {
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [attendance, setAttendance] = useState({});
  const [existingAttendance, setExistingAttendance] = useState([]);
  const [showMarkedAttendance, setShowMarkedAttendance] = useState(false);
  const [markedAttendanceData, setMarkedAttendanceData] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('marking');
  
  // Reports state
  const [loading, setLoading] = useState(false);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [percentageData, setPercentageData] = useState(null);
  const [defaulters, setDefaulters] = useState(null);
  const [dateRangeReport, setDateRangeReport] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadBatches();
  }, []);

  useEffect(() => {
    if (selectedBatch) {
      loadStudents();
      loadExistingAttendance();
    }
  }, [selectedBatch, selectedDate]);

  const loadBatches = async () => {
    try {
      const response = await axios.get(`${API}/batches`);
      setBatches(response.data);
    } catch (error) {
      toast.error('Failed to load batches');
    }
  };

  const loadStudents = async () => {
    try {
      const token = localStorage.getItem('token');
      const dateStr = selectedDate.toISOString();
      
      // Get students scheduled for this date based on batch schedules
      const response = await axios.get(
        `${API}/attendance/scheduled-students?date=${dateStr}&batch_id=${selectedBatch}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setStudents(response.data);
    } catch (error) {
      console.error('Failed to load students:', error);
      toast.error('Failed to load scheduled students');
      setStudents([]);
    }
  };

  const loadExistingAttendance = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/attendance/batch/${selectedBatch}?date=${dateStr}`);
      const attendanceMap = {};
      response.data.forEach(att => {
        attendanceMap[att.student_id] = att.status;
      });
      setExistingAttendance(response.data);
      setAttendance(attendanceMap);
      setMarkedAttendanceData(response.data);
      
      // Show marked attendance if there are existing records
      if (response.data.length > 0) {
        setShowMarkedAttendance(true);
      }
    } catch (error) {
      console.error('Failed to load existing attendance');
      setAttendance({});
    }
  };

  const handleAttendanceChange = (studentId, status) => {
    setAttendance(prev => ({ ...prev, [studentId]: status }));
  };

  const handleMarkAllPresent = () => {
    const allPresent = {};
    students.forEach(student => {
      allPresent[student.id] = 'present';
    });
    setAttendance(allPresent);
    toast.success('All students marked as present');
  };

  const handleMarkAllAbsent = () => {
    const allAbsent = {};
    students.forEach(student => {
      allAbsent[student.id] = 'absent';
    });
    setAttendance(allAbsent);
    toast.success('All students marked as absent');
  };

  const handleSaveAttendance = async () => {
    try {
      const records = Object.entries(attendance).map(([studentId, status]) => ({
        student_id: studentId,
        batch_id: selectedBatch,
        date: selectedDate.toISOString().split('T')[0],
        status
      }));

      await Promise.all(
        records.map(record => axios.post(`${API}/attendance`, record))
      );

      toast.success('Attendance marked successfully!');
      await loadExistingAttendance(); // Reload to show marked attendance
      setShowMarkedAttendance(true); // Show the marked attendance view
    } catch (error) {
      toast.error('Failed to mark attendance');
    }
  };

  // Report functions
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const fetchMonthlyReport = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/attendance/reports/monthly?month=${selectedMonth}&year=${selectedYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMonthlyReport(response.data);
      toast.success('Monthly report loaded');
    } catch (error) {
      console.error('Error fetching monthly report:', error);
      toast.error('Failed to load monthly report');
    } finally {
      setLoading(false);
    }
  };

  const fetchPercentageReport = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/attendance/reports/percentage`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPercentageData(response.data);
      toast.success('Percentage report loaded');
    } catch (error) {
      console.error('Error fetching percentage report:', error);
      toast.error('Failed to load percentage report');
    } finally {
      setLoading(false);
    }
  };

  const fetchDefaulters = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/attendance/reports/defaulters?threshold=75`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDefaulters(response.data);
      toast.success('Defaulters list loaded');
    } catch (error) {
      console.error('Error fetching defaulters:', error);
      toast.error('Failed to load defaulters');
    } finally {
      setLoading(false);
    }
  };

  const fetchDateRangeReport = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/attendance/reports/date-range?start_date=${startDate}&end_date=${endDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDateRangeReport(response.data);
      toast.success('Date range report loaded');
    } catch (error) {
      console.error('Error fetching date range report:', error);
      toast.error('Failed to load date range report');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = (data, filename) => {
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => Object.values(row).join(',')).join('\n');
    const csv = `${headers}\n${rows}`;
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
    toast.success('Report exported successfully');
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="attendance-marking">
      <Card className="p-6 bg-white shadow-lg border-0">
        <h3 className="text-lg font-semibold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Attendance Management
        </h3>

        <Tabs defaultValue="marking" className="w-full">
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="marking">Mark Attendance</TabsTrigger>
            <TabsTrigger value="monthly">Monthly Report</TabsTrigger>
            <TabsTrigger value="percentage">Percentage</TabsTrigger>
            <TabsTrigger value="defaulters">Defaulters</TabsTrigger>
            <TabsTrigger value="daterange">Date Range</TabsTrigger>
          </TabsList>

          {/* Mark Attendance Tab */}
          <TabsContent value="marking" className="space-y-6">
            {/* Top Selection Bar */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Select Batch
                  </Label>
                  <Select value={selectedBatch} onValueChange={setSelectedBatch}>
                    <SelectTrigger data-testid="batch-select" className="bg-white">
                      <SelectValue placeholder="Choose a batch..." />
                    </SelectTrigger>
                    <SelectContent>
                      {batches.map(batch => (
                        <SelectItem key={batch.id} value={batch.id}>{batch.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="attendance-date" className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4" />
                    Select Date
                  </Label>
                  <input
                    id="attendance-date"
                    type="date"
                    value={selectedDate ? selectedDate.toISOString().split('T')[0] : ''}
                    onChange={(e) => setSelectedDate(e.target.value ? new Date(e.target.value) : new Date())}
                    className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="attendance-date-input"
                  />
                </div>
              </div>
            </div>

            {selectedBatch && students.length > 0 && !showMarkedAttendance && (
              <>
                {/* Quick Actions */}
                <div className="flex items-center justify-between bg-white p-4 rounded-lg border shadow-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-700">Quick Actions:</span>
                    <Button 
                      onClick={handleMarkAllPresent} 
                      size="sm" 
                      variant="outline"
                      className="text-green-600 border-green-300 hover:bg-green-50"
                    >
                      <UserCheck className="w-4 h-4 mr-2" />
                      Mark All Present
                    </Button>
                    <Button 
                      onClick={handleMarkAllAbsent} 
                      size="sm" 
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <UserX className="w-4 h-4 mr-2" />
                      Mark All Absent
                    </Button>
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-semibold">{Object.keys(attendance).length}</span> / {students.length} marked
                  </div>
                </div>

                {/* Student Grid - Simplified */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {students.map(student => (
                    <Card 
                      key={student.id} 
                      className={`p-4 transition-all duration-200 cursor-pointer ${
                        attendance[student.id] === 'present' 
                          ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-300 shadow-md' 
                          : attendance[student.id] === 'absent'
                          ? 'bg-gradient-to-br from-red-50 to-rose-50 border-red-300 shadow-md'
                          : 'hover:shadow-md border-gray-200'
                      }`}
                      data-testid={`student-attendance-${student.id}`}
                    >
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{student.name}</h4>
                            <p className="text-xs text-gray-500">{student.gk_code || 'No Code'}</p>
                          </div>
                          {attendance[student.id] && (
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              attendance[student.id] === 'present' ? 'bg-green-500' : 'bg-red-500'
                            }`}>
                              {attendance[student.id] === 'present' ? (
                                <CheckCircle className="w-6 h-6 text-white" />
                              ) : (
                                <XCircle className="w-6 h-6 text-white" />
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleAttendanceChange(student.id, 'present')}
                            size="sm"
                            className={`flex-1 ${
                              attendance[student.id] === 'present' 
                                ? 'bg-green-600 hover:bg-green-700 text-white' 
                                : 'bg-white border border-green-300 text-green-600 hover:bg-green-50'
                            }`}
                            data-testid={`present-${student.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Present
                          </Button>
                          <Button
                            onClick={() => handleAttendanceChange(student.id, 'absent')}
                            size="sm"
                            className={`flex-1 ${
                              attendance[student.id] === 'absent' 
                                ? 'bg-red-600 hover:bg-red-700 text-white' 
                                : 'bg-white border border-red-300 text-red-600 hover:bg-red-50'
                            }`}
                            data-testid={`absent-${student.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Absent
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>

                {/* Save Button */}
                <div className="sticky bottom-0 bg-white p-4 rounded-lg border shadow-lg">
                  <Button
                    onClick={handleSaveAttendance}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-6 text-lg font-semibold"
                    disabled={Object.keys(attendance).length === 0}
                    data-testid="save-attendance-button"
                  >
                    <Save className="w-5 h-5 mr-3" />
                    Save Attendance ({Object.keys(attendance).length} students)
                  </Button>
                </div>
              </>
            )}

            {/* View Marked Attendance Section - Simplified */}
            {showMarkedAttendance && markedAttendanceData.length > 0 && (
              <div className="space-y-4">
                {/* Header with Stats */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 rounded-xl text-white">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-2xl font-bold">Attendance Saved!</h4>
                      <p className="text-blue-100 text-sm">Click on any student card to toggle their status</p>
                    </div>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setShowMarkedAttendance(false);
                        // Keep the existing attendance data for editing
                      }}
                      className="bg-white text-blue-600 hover:bg-blue-50"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Edit All
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg">
                      <p className="text-blue-100 text-xs">Total Students</p>
                      <p className="text-2xl font-bold">{markedAttendanceData.length}</p>
                    </div>
                    <div className="bg-green-500/30 backdrop-blur-sm p-3 rounded-lg">
                      <p className="text-green-100 text-xs">Present</p>
                      <p className="text-2xl font-bold">{markedAttendanceData.filter(r => r.status === 'present').length}</p>
                    </div>
                    <div className="bg-red-500/30 backdrop-blur-sm p-3 rounded-lg">
                      <p className="text-red-100 text-xs">Absent</p>
                      <p className="text-2xl font-bold">{markedAttendanceData.filter(r => r.status === 'absent').length}</p>
                    </div>
                  </div>
                </div>

                {/* Student Cards - Quick Edit */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {markedAttendanceData.map((record) => {
                    const student = students.find(s => s.id === record.student_id);
                    return (
                      <Card 
                        key={record.id} 
                        className={`p-4 cursor-pointer transition-all duration-200 ${
                          record.status === 'present' 
                            ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-300' 
                            : 'bg-gradient-to-br from-red-50 to-rose-50 border-red-300'
                        } hover:shadow-lg`}
                        onClick={async () => {
                          try {
                            const newStatus = record.status === 'present' ? 'absent' : 'present';
                            await axios.post(`${API}/attendance`, {
                              student_id: record.student_id,
                              batch_id: selectedBatch,
                              date: selectedDate.toISOString().split('T')[0],
                              status: newStatus
                            });
                            toast.success(`Changed to ${newStatus}!`);
                            loadExistingAttendance();
                          } catch (error) {
                            toast.error('Failed to update');
                          }
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{record.student_name || student?.name || 'Unknown'}</h4>
                            <p className="text-xs text-gray-500">{student?.gk_code || 'No Code'}</p>
                          </div>
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            record.status === 'present' ? 'bg-green-500' : 'bg-red-500'
                          }`}>
                            {record.status === 'present' ? (
                              <CheckCircle className="w-7 h-7 text-white" />
                            ) : (
                              <XCircle className="w-7 h-7 text-white" />
                            )}
                          </div>
                        </div>
                        <div className="mt-2">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                            record.status === 'present' 
                              ? 'bg-green-200 text-green-800' 
                              : 'bg-red-200 text-red-800'
                          }`}>
                            {record.status.toUpperCase()}
                          </span>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {selectedBatch && students.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-2">No students scheduled for this day</p>
                <p className="text-sm text-gray-400">This batch may not have classes on {format(selectedDate, 'EEEE')}</p>
              </div>
            )}

            {!selectedBatch && (
              <p className="text-gray-500 text-center py-8">Please select a batch to mark attendance</p>
            )}
          </TabsContent>

          {/* Monthly Report Tab - Copied from AttendanceReports.js */}
          <TabsContent value="monthly" className="space-y-4">
            <div className="flex gap-4 items-end">
              <div>
                <Label>Month</Label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  {months.map((month, idx) => (
                    <option key={idx} value={idx + 1}>{month}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Year</Label>
                <Input
                  type="number"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="w-32"
                />
              </div>
              <Button onClick={fetchMonthlyReport} disabled={loading}>
                <BarChart className="w-4 h-4 mr-2" />
                Generate Report
              </Button>
            </div>

            {monthlyReport && monthlyReport.report.length > 0 && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <Card className="p-4 bg-blue-50">
                    <p className="text-sm text-gray-600">Total Students</p>
                    <p className="text-2xl font-bold text-blue-600">{monthlyReport.total_students}</p>
                  </Card>
                  <Card className="p-4 bg-green-50">
                    <p className="text-sm text-gray-600">Month</p>
                    <p className="text-xl font-bold text-green-600">
                      {months[monthlyReport.month - 1]} {monthlyReport.year}
                    </p>
                  </Card>
                  <Card className="p-4">
                    <Button 
                      onClick={() => exportToExcel(monthlyReport.report, `monthly_report_${monthlyReport.month}_${monthlyReport.year}`)}
                      variant="outline"
                      className="w-full"
                    >
                      <FileDown className="w-4 h-4 mr-2" />
                      Export
                    </Button>
                  </Card>
                </div>

                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ReBarChart data={monthlyReport.report.slice(0, 10)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="student_name" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="percentage" fill="#10b981" name="Attendance %" />
                    </ReBarChart>
                  </ResponsiveContainer>
                </div>

                <div className="border rounded-lg overflow-auto max-h-96">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left">Student Name</th>
                        <th className="px-4 py-2 text-center">Total</th>
                        <th className="px-4 py-2 text-center">Present</th>
                        <th className="px-4 py-2 text-center">Absent</th>
                        <th className="px-4 py-2 text-center">Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyReport.report.map((student, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-4 py-2">{student.student_name}</td>
                          <td className="px-4 py-2 text-center">{student.total_classes}</td>
                          <td className="px-4 py-2 text-center text-green-600">{student.present}</td>
                          <td className="px-4 py-2 text-center text-red-600">{student.absent}</td>
                          <td className="px-4 py-2 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              student.percentage >= 90 ? 'bg-green-100 text-green-800' :
                              student.percentage >= 75 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {student.percentage}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Percentage Tab */}
          <TabsContent value="percentage" className="space-y-4">
            <Button onClick={fetchPercentageReport} disabled={loading}>
              <TrendingUp className="w-4 h-4 mr-2" />
              Load Attendance Percentage
            </Button>

            {percentageData && percentageData.data.length > 0 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card className="p-4 bg-purple-50">
                    <p className="text-sm text-gray-600">Total Students</p>
                    <p className="text-2xl font-bold text-purple-600">{percentageData.total_students}</p>
                  </Card>
                  <Card className="p-4">
                    <Button 
                      onClick={() => exportToExcel(percentageData.data, 'attendance_percentage')}
                      variant="outline"
                      className="w-full"
                    >
                      <FileDown className="w-4 h-4 mr-2" />
                      Export
                    </Button>
                  </Card>
                </div>

                <div className="border rounded-lg overflow-auto max-h-96">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left">Rank</th>
                        <th className="px-4 py-2 text-left">Student Name</th>
                        <th className="px-4 py-2 text-center">Total</th>
                        <th className="px-4 py-2 text-center">Present</th>
                        <th className="px-4 py-2 text-center">Absent</th>
                        <th className="px-4 py-2 text-center">Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {percentageData.data.map((student, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-4 py-2">#{idx + 1}</td>
                          <td className="px-4 py-2">{student.student_name}</td>
                          <td className="px-4 py-2 text-center">{student.total}</td>
                          <td className="px-4 py-2 text-center text-green-600">{student.present}</td>
                          <td className="px-4 py-2 text-center text-red-600">{student.absent}</td>
                          <td className="px-4 py-2 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              student.percentage >= 90 ? 'bg-green-100 text-green-800' :
                              student.percentage >= 75 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {student.percentage}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Defaulters Tab */}
          <TabsContent value="defaulters" className="space-y-4">
            <Button onClick={fetchDefaulters} disabled={loading}>
              <AlertTriangle className="w-4 h-4 mr-2" />
              Load Defaulters (Below 75%)
            </Button>

            {defaulters && (
              <div className="space-y-4">
                <Card className="p-4 bg-red-50">
                  <p className="text-sm text-gray-600">Total Defaulters (Below 75%)</p>
                  <p className="text-2xl font-bold text-red-600">{defaulters.total_defaulters}</p>
                </Card>

                {defaulters.defaulters.length > 0 ? (
                  <div className="border rounded-lg overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-red-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left">Student Name</th>
                          <th className="px-4 py-2 text-left">GK Code</th>
                          <th className="px-4 py-2 text-left">Contact</th>
                          <th className="px-4 py-2 text-center">Total</th>
                          <th className="px-4 py-2 text-center">Present</th>
                          <th className="px-4 py-2 text-center">Absent</th>
                          <th className="px-4 py-2 text-center">Percentage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {defaulters.defaulters.map((student, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-4 py-2">{student.student_name}</td>
                            <td className="px-4 py-2">{student.gk_code || 'N/A'}</td>
                            <td className="px-4 py-2">{student.contact_number || 'N/A'}</td>
                            <td className="px-4 py-2 text-center">{student.total}</td>
                            <td className="px-4 py-2 text-center text-green-600">{student.present}</td>
                            <td className="px-4 py-2 text-center text-red-600">{student.absent}</td>
                            <td className="px-4 py-2 text-center">
                              <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-800">
                                {student.percentage}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No defaulters found! 🎉</p>
                )}
              </div>
            )}
          </TabsContent>

          {/* Date Range Tab */}
          <TabsContent value="daterange" className="space-y-4">
            <div className="flex gap-4 items-end">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <Button onClick={fetchDateRangeReport} disabled={loading}>
                <CalendarIcon className="w-4 h-4 mr-2" />
                Generate Report
              </Button>
            </div>

            {dateRangeReport && dateRangeReport.daily_trends && dateRangeReport.daily_trends.length > 0 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card className="p-4 bg-indigo-50">
                    <p className="text-sm text-gray-600">Total Records</p>
                    <p className="text-2xl font-bold text-indigo-600">{dateRangeReport.total_records}</p>
                  </Card>
                  <Card className="p-4">
                    <Button 
                      onClick={() => exportToExcel(dateRangeReport.student_wise, 'date_range_report')}
                      variant="outline"
                      className="w-full"
                    >
                      <FileDown className="w-4 h-4 mr-2" />
                      Export
                    </Button>
                  </Card>
                </div>

                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dateRangeReport.daily_trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="percentage" stroke="#10b981" name="Attendance %" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
};

export default AttendanceMarking;